// Entregas detectadas por el modelo nuevo (iaV3: RF-DETR, licencia Apache-2.0): camiones, volquetes, mixers y
// materiales nuevos (pallets, bolsones, madera, hierros, arena, piedra) con foto de antes / llegada / después.
// Las arma un servicio aparte (1 foto cada 20 min por cámara) y las sirve el backend en /api/entregas.
// Si la cámara todavía no tiene iaV3, el Calendario sigue usando la heurística vieja de entregas.js.
// En local se apunta a otro backend con REACT_APP_ENTREGAS_API_URL.
import axios from 'axios';

const BASE = process.env.REACT_APP_ENTREGAS_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081';
const api = axios.create({ baseURL: `${BASE.replace(/\/$/, '')}/api/entregas`, timeout: 15000 });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authLisualToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const MATERIAL_LABEL = {
  pallet_material: 'pallets', bolson_bolsas: 'bolsones', madera_tablas: 'madera',
  hierros_malla: 'hierros', pila_arena_tierra: 'arena / piedra', pila_grava: 'piedra', perfil_cano_metal: 'caños / perfiles',
};
export const MATERIAL_ICON = { pallet_material: '📦', bolson_bolsas: '🛍️', madera_tablas: '🌲', hierros_malla: '🔩', pila_arena_tierra: '⛰️', pila_grava: '🥌', perfil_cano_metal: '📏' };
// Mismos nombres de clase que usa el resto del Calendario (colores y textos de iaData / entregas.js)
const CLS = { camion: 'Camion', volquete: 'Camion volquete', mixer: 'Mixer', camioneta: 'Camioneta', excavadora: 'Excavadora', minicargadora: 'Minicargadora' };

const lista = (xs) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`);
export const textoMateriales = (ms) => lista([...new Set((ms || []).map((m) => MATERIAL_LABEL[m.cls] || m.cls))]);

const foto = (f) => (f ? { id: f.id || f.key, ms: f.ts, s3: f.key, w: f.w || 1920, h: f.h || 1080 } : null);

const materialMirado = (e) => (e.material_verif ? e.material_verif === 'ok' : String(e.verificado || '').startsWith('claude'));

// Evento del backend -> la misma forma que las "visitas" de entregas.js, para reusar la tarjeta del diario.
export function aVisita(e) {
  const ref = e.fotos?.llegada || e.fotos?.despues || e.fotos?.antes || {};
  const w = ref.w || 1920, h = ref.h || 1080;
  const b = e.box || [0, 0, 1, 1];
  return {
    id: e.key,
    v3: true,
    day: e.dia,
    cls: e.vehiculo ? CLS[e.vehiculo] || e.vehiculo : 'Material',
    tipo: e.tipo,
    arrive: e.inicio,
    leave: e.fin,
    mins: e.min,
    nPhotos: e.nFotos,
    maxConf: e.score,
    confianza: e.confianza,
    hormigonado: !!e.hormigonado,
    // El tipo de material solo se muestra si alguien lo MIRÓ en las fotos (verificación visual o confirmación de Claude);
    // la clase del detector sola no (confunde madera con piedra, cambios de luz…).
    materiales: materialMirado(e) ? e.materiales || [] : [],
    carga: materialMirado(e) ? e.carga || [] : [],
    texto: e.texto,
    sinConfirmar: e.verificado === 'sin confirmar' || e.verificado === 'sin_verificar' || e.verificado === 'sin verificar', // el servicio iav3 escribe 'sin_verificar'
    // Material mirado en el recorte de llegada + después (no la clase del detector): 'ok' | 'nada' | 'sin_mirar'
    materialVerif: e.material_verif || null,
    juez: e.juez || null,
    descripcion: e.descripcion || '',
    box: { x1: b[0] * w, y1: b[1] * h, x2: b[2] * w, y2: b[3] * h },
    sinCaja: !e.box,
    w, h,
    before: foto(e.fotos?.antes),
    arrival: foto(e.fotos?.llegada),
    after: foto(e.fotos?.despues),
    marca: e.marca || null,
    parked: false,
  };
}

// Título de la tarjeta: lo que llegó primero, el vehículo después.
export function titulo(v) {
  const mats = textoMateriales(v.materiales);
  if (v.tipo === 'material') return `Aparecieron ${mats} nuevos en la obra`;
  if (v.materialVerif === 'nada' && !mats && !['Mixer', 'bomba_hormigon'].includes(v.cls)) return v.cls === 'Camioneta' ? 'Visita de camioneta' : 'Visita de camión';
  if (v.tipo === 'maquina') return `Entró una ${v.cls === 'Minicargadora' ? 'minicargadora' : 'excavadora'}`;
  if (v.cls === 'Mixer') return v.hormigonado ? `Hormigonado probable: mixer ${v.mins} min en obra` : 'Llegó un mixer';
  if (v.cls === 'bomba_hormigon') return v.hormigonado ? `Hormigonado probable: bomba de hormigón ${v.mins} min en obra` : 'Llegó una bomba de hormigón';
  if (v.cls === 'grua_movil') return 'Llegó un camión grúa';
  const veh = v.cls === 'Camion volquete' ? 'un camión volquete' : v.cls === 'Camioneta' ? 'una camioneta' : 'un camión';
  const carga = textoMateriales(v.carga);
  if (mats) return `Llegaron ${mats} (${veh})`;
  return carga ? `Llegó ${veh} cargado con ${carga}` : `Llegó ${veh}`;
}

const cache = new Map();
// { fuente: 'iaV3'|'ninguna', analizadoDesde, porDia: Map(dia -> visitas[]) } — nunca tira error (si falla, "ninguna")
export function cargarEntregas(feedId, desde, hasta) {
  if (!feedId) return Promise.resolve({ fuente: 'ninguna', porDia: new Map() });
  const ck = `${feedId}|${desde}|${hasta}`;
  if (!cache.has(ck)) {
    const p = api.get(`/${encodeURIComponent(feedId)}`, { params: { desde, hasta } })
      .then(({ data }) => {
        const porDia = new Map();
        for (const e of data.eventos || []) {
          // Material que "apareció" sin un vehículo a la vista: no se muestra (demasiado frágil: luz, sombras, el detector
          // que no lo vio en la foto de antes). Solo cuenta lo que llega con una visita.
          if (e.tipo === 'material') continue;
          const v = aVisita(e);
          if (!porDia.has(v.day)) porDia.set(v.day, []);
          porDia.get(v.day).push(v);
        }
        return { fuente: data.fuente, analizadoDesde: data.analizadoDesde || null, porDia, resumenes: data.resumenes || {} };
      })
      .catch(() => { cache.delete(ck); return { fuente: 'ninguna', porDia: new Map(), error: true }; });
    cache.set(ck, p);
    setTimeout(() => cache.delete(ck), 5 * 60e3); // hoy puede sumar entregas
  }
  return cache.get(ck);
}

// "Fue entrega" / "No fue" / qué llegó: queda en Firestore (entregasFeedback) para reentrenar el modelo.
export const guardarMarca = (feedId, key, { estado = null, nota = '' } = {}) =>
  api.put(`/${encodeURIComponent(feedId)}/feedback/${encodeURIComponent(key)}`, { estado, nota }).then((r) => r.data);

// ---------- Reglas "Avisame cuando…" e historial de avisos (adicional Alertas de entregas) ----------
export const reglasApi = {
  leer: (feedId) => api.get(`/${encodeURIComponent(feedId)}/reglas`).then((r) => r.data),
  guardar: (feedId, reglas) => api.put(`/${encodeURIComponent(feedId)}/reglas`, { reglas }).then((r) => r.data),
  avisos: (feedId) => api.get(`/${encodeURIComponent(feedId)}/avisos`).then((r) => r.data.avisos || []),
  votar: (feedId, id, voto) => api.put(`/${encodeURIComponent(feedId)}/avisos/${encodeURIComponent(id)}/voto`, { voto }).then((r) => r.data),
};

// Memoria del día pulida con IA (Claude Haiku, con caché en el servidor). Si falla, queda la plantilla.
export const pulirMemoria = (feedId, dia, base, datos) =>
  api.post(`/${encodeURIComponent(feedId)}/diario/${dia}`, { base, datos }, { timeout: 25000 }).then((r) => r.data).catch(() => ({ texto: base, ia: false }));
